import * as React from 'react';
import {
  Box, ButtonBase, Tooltip, useTheme,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { Logger } from '../config/logging/logger';

interface CopyPillProps{
  label: string; // what to show on the left (e.g., "JOIST")
  copyText?: string; // what to copy (defaults to label)
  onCopied?: (text: string) => void;
  copiedMs?: number; // how long to show "Copied!" state
}

export const CopyPill = ({
  label,
  copyText,
  onCopied,
  copiedMs = 1200,
}:CopyPillProps) => {
  const theme = useTheme();
  const logger = Logger.create('CopyPill');
  const [copied, setCopied] = React.useState(false);

  const doCopy = async () => {
    const text = copyText ?? label;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy'); // deprecated, but only fallback
        document.body.removeChild(ta);
      }
      setCopied(true);
      onCopied?.(text);
      window.setTimeout(() => setCopied(false), copiedMs);
    } catch (error) {
      logger.error(Logger.format(error));
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <ButtonBase
        onClick={doCopy}
        aria-label={`Copy ${label} to clipboard`}
        sx={{
          display: 'inline-flex',
          borderRadius: theme.spacing(1.5),
          overflow: 'hidden',
          // subtle shadow like your screenshot
          boxShadow:
            '0 0 1px 1px rgba(0,0,0,0.04), 0 0 4px 0 rgba(0,0,0,0.02), 0 0 8px 0 rgba(0,0,0,0.04)',
          bgcolor: theme.palette.grey[100],
        }}
      >
        {/* Left: label */}
        <Box
          sx={{
            p: theme.spacing(1),
            fontFamily: 'Geist Mono',
            fontSize: theme.spacing(2),
            fontStyle: 'normal',
            fontWeight: 500,
            color: theme.palette.grey[900],
            bgcolor: theme.palette.grey[300],
          }}
        >
          {label}
        </Box>

        {/* Right: icon segment */}
        <Box
          sx={{
            p: theme.spacing(1),
            display: 'grid',
            placeItems: 'center',
            bgcolor: theme.palette.grey[300],
            borderLeft: `1px solid ${theme.palette.grey[300]}`,
            minWidth: 36,
          }}
        >
          {copied ? (
            <CheckOutlinedIcon fontSize="small" />
          ) : (
            <ContentCopyOutlinedIcon fontSize="small" />
          )}
        </Box>
      </ButtonBase>
    </Tooltip>
  );
};
